<template>
  <n-input
    ref="inputRef"
    class="etg-input"
    :input-props="{ enterKeyHint }"
    :resizable="false"
    :autosize="props.autosize"
    v-bind="$attrs"
    :value="inputValue"
    @update:value="handleUpdateValue"
    @keydown.enter="handleEnterKeydown"
    @focus="handleFocus"
    @blur="handleBlur"
    @clear="handleClear"
    v-on.stop="events"
  >
    <template #clear-icon>
      <div class="remove-wrapper flex items-center justify-center">
        <svg-icon name="remove" class="size-4" />
      </div>
    </template>

    <template #password-visible-icon>
      <svg-icon name="eyes-close" class="size-6 p-1" />
    </template>
    <template #password-invisible-icon>
      <svg-icon name="eyes-open" class="size-6 p-1" />
    </template>

    <template v-for="(_, name) in slots" v-slot:[name]>
      <slot :name="name"></slot>
    </template>
  </n-input>
</template>

<script lang="ts" setup>
import { NInput } from 'naive-ui'
import type { CompositionEventEmits, EtgInputProps } from '@/types/component'

interface Emits extends CompositionEventEmits {
  (event: 'submit', value?: string): void
  (event: 'focus'): void
  (event: 'blur'): void
  (event: 'clear'): void
}

const slots = defineSlots()

const { isComposing, events } = useCompositionEvent()
const inputRef = ref<Nullable<typeof NInput>>(null)

const inputValue = defineModel<string>()

const props = defineProps<EtgInputProps>()
const emit = defineEmits<Emits>()

const enterKeyHint = computed(() => (props.nextEleRef ? 'next' : props.enterKeyHint))

const handleUpdateValue = (value: string) => {
  inputValue.value = value
}

const handleEnterKeydown = function () {
  if (isComposing.value) return
  if (props.nextEleRef && typeof props.nextEleRef?.focus === 'function') {
    props.nextEleRef.focus()
    return
  }

  emit('submit', inputValue.value)
}

const handleFocus = () => emit('focus')
const handleBlur = () => emit('blur')
const handleClear = () => emit('clear')

const blur = (...arg: any) => inputRef.value?.blur?.(...arg) || null
const focus = (...arg: any) => inputRef.value?.focus?.(...arg) || false
const scrollTo = (...arg: any) => inputRef.value?.scrollIntoView?.(...arg) || false

defineExpose({
  blur,
  focus,
  scrollTo,
})
</script>

<style lang="scss" scoped>
.etg-input {
  &.n-input.n-input--error-status:not(.n-input--disabled) {
    @apply bg-red-00;
  }
  &:not(:is(.n-input--disabled, .n-input--focus, .n-input--error-status)):hover :deep(.n-input__state-border) {
    @apply <md:border-transparent;
  }
}
</style>
